import { InternalChargeCardAccount } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import { useToggleSafePay } from 'features/believe/learningCenter/hooks/useToggleSafePay';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const SNACKBAR_TIMEOUT = 2750;

const SafePayScreen = () => {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'safePayString',
  });

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { refetch, loading, accountDetails } = useAccountDetails(id);
  const {
    toggleSafePay,
    loading: fetching,
    errorModel,
    response,
  } = useToggleSafePay();

  const isSafePayOn: boolean = (accountDetails as InternalChargeCardAccount)
    ?.isSafePayEnabled;

  const toggleStateMassage =
    response?.toggleSafeCreditBuilder?.message === 'success' && isSafePayOn;

  const toggleOffState =
    response?.toggleSafeCreditBuilder?.message === 'success' && !isSafePayOn;

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_TURN_ON_SAFE_PAY_CHARGE_CARD,
  ]);

  useEffect(() => {
    refetch();
    refreshTiles();
  }, [refetch, response, refreshTiles]);

  return (
    <View style={styles.screenContainer}>
      <Stack.Screen
        options={{
          title: translatedText('headerTitle'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {toggleStateMassage || toggleOffState ? (
        <Snackbar
          duration={SNACKBAR_TIMEOUT}
          text={translatedText(
            toggleStateMassage ? 'successMassage' : 'turnedOffMessage',
          )}
        />
      ) : null}
      <View style={styles.listStyle}>
        <ListItem
          secondaryNumberOfLines={0}
          showsChevron={false}
          primaryCopy={translatedText('safePayText')}
          secondaryCopy={translatedText('safePayOn')}
          trailingAccessory={{
            id: '1',
            name: VXLComponentId.SWITCH,
            props: {
              isOn: (isSafePayOn as boolean) || false,
              onValueChange: (safePayStatus) => {
                toggleSafePay(safePayStatus as boolean);
              },
              analyticsId: isSafePayOn ? 'safePayOff' : 'safePayOn',
              customAttributes: {
                account_type: 'charge_card',
              },
            },
          }}
        />
      </View>
      <ScreenTransitionOverlay
        isLoading={loading || fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel ? (
            <View>
              <DefaultPageDownErrorMessage
                error={errorModel}
                title={defaultTitle}
                message={defaultMessage}
                tryAgainAction={refetch}
              />
            </View>
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  listStyle: {
    flex: 1,
    width: '100%',
  },
  screenContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
});

export default SafePayScreen;
